/**
 * @fileoverview Custom hook for querying template versions.
 */

import {useQuery, UseQueryOptions} from "react-query";
import Alert from "react-s-alert";

import {AdmissionsService} from "../services/AdmissionsService";
import {DocumentService} from "../services/DocumentService";

/**
 * Custom hook that fetches the template version of an admission (when admissionId provided) 
 * or the latest version for the facility.
 * 
 * @param {Object} params The hook parameters.
 * @param {string} params.facilityId The ID of the facility.
 * @param {string} [params.admissionId] Optional admission ID to get specific template version.
 * @param {UseQueryOptions} [params.options] Additional React Query options.
 * @returns {Object} React Query result containing template version and admission data.
 */
export const useTemplateVersionQuery = ({
  facilityId,
  admissionId,
  options,
}: {
  facilityId: string;
  admissionId?: string;
  options?: UseQueryOptions<{
    admission?: any;
    version: any;
  }>[];
}) =>
  useQuery(
    ["templateVersion", facilityId, admissionId],
    async () => {
      if (admissionId) {
        const {data: admission} = await AdmissionsService.get(admissionId);

        const {data: version} = await DocumentService.getTemplateVersion(
          facilityId,
          admission.templateId,
        );

        return {admission, version};
      }

      const {data: version} = await DocumentService.getLatestTemplateVersion(
        facilityId,
      );

      return {version};
    },
    {
      placeholderData: {version: undefined, admission: undefined},
      onError: () =>
        Alert.error(
          "Failed to load template version. Please, try again later.",
        ),
      ...options,
    },
  );
